import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, List, ListItem, ListItemButton, ListItemText, Paper, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { debounce } from 'lodash';

function SearchVehicle({ onVehicleSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFocus, setHasFocus] = useState(false);

  // Reference to the search container for click outside detection
  const searchContainerRef = useRef(null);

  // Create a debounced function to prevent too many API calls
  const debouncedSearch = useRef(
    debounce(async (searchQuery) => {
      if (searchQuery.length < 2) {
        setResults([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`${process.env.REACT_APP_VALHALLA_URL_BACKEND}/api/vehicles/search?query=${searchQuery}`);
        if (response.data.success) {
          setResults(response.data.data);
        } else {
          setError(response.data.error || 'Error en la búsqueda');
          setResults([]);
        }
      } catch (err) {
        setError('Error de conexión al servidor');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 500)
  ).current;

  // Effect for handling the search
  useEffect(() => {
    if (query) {
      debouncedSearch(query);
    } else {
      setResults([]);
    }

    // Cleanup debounce on unmount
    return () => {
      debouncedSearch.cancel();
    };
  }, [query, debouncedSearch]);

  // Effect for handling click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setHasFocus(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (event) => {
    setQuery(event.target.value);
  };

  const handleFocus = () => {
    setHasFocus(true);
  };

  const handleVehicleSelect = (vehicle) => {
    onVehicleSelect(vehicle);
    setHasFocus(false);
  };

  return (
    <Box
      ref={searchContainerRef}
      sx={{
        position: 'relative',
        width: '100%'
      }}
    >
      <TextField
        fullWidth
        label="Buscar patente"
        variant="outlined"
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        placeholder="Ingrese al menos 2 caracteres"
        sx={{ mb: 1 }}
      />

      {hasFocus && (query.length >= 2 || loading || error || results.length > 0) && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            zIndex: 1000,
            width: '100%',
            maxHeight: 300,
            overflow: 'auto',
            mt: 0.5
          }}
        >
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && (
            <Box sx={{ p: 2 }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {!loading && !error && results.length === 0 && query.length >= 2 && (
            <Box sx={{ p: 2 }}>
              <Typography>No se encontraron resultados</Typography>
            </Box>
          )}

          {!loading && results.length > 0 && (
            <List sx={{ py: 0 }}>
              {results.map((vehicle) => (
                <ListItem key={vehicle.id} disablePadding divider>
                  <ListItemButton onClick={() => handleVehicleSelect(vehicle)}>
                    <ListItemText
                      primary={vehicle.patente}
                      secondary={
                        <>
                          {vehicle.marca} {vehicle.modelo} ({vehicle.anio})
                          <br />
                          Usuario: {vehicle.user}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default SearchVehicle; 